import { useCallback, useMemo, useRef, useState } from 'react';

/**
 * Save changes / Cancel behaviour shared by every permission editor (member Studios now, the
 * role builder in CT-5613): `isSaving` blocks a double submit, Cancel closes silently when
 * nothing changed and otherwise asks first. `onSave` resolves `true` when it worked (the editor
 * then closes) and `false` when it failed (the edits stay; the caller already showed the error).
 *
 * @param {object} args
 * @param {boolean} args.isDirty
 * @param {() => Promise<boolean>} args.onSave
 * @param {() => void} args.onClose Leaves edit mode.
 */
export const useEditorSession = ({ isDirty, onSave, onClose }) => {
  const [isSaving, setIsSaving] = useState(false);
  const [isConfirmingDiscard, setIsConfirmingDiscard] = useState(false);
  // The ref blocks a second click before React re-renders with `isSaving`.
  const savingRef = useRef(false);

  const save = useCallback(async () => {
    if (savingRef.current) return;
    savingRef.current = true;
    setIsSaving(true);
    let ok = false;
    try {
      ok = await onSave();
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
    if (ok) onClose();
  }, [onSave, onClose]);

  const cancel = useCallback(() => {
    if (isDirty) setIsConfirmingDiscard(true);
    else onClose();
  }, [isDirty, onClose]);

  const keepEditing = useCallback(() => setIsConfirmingDiscard(false), []);
  const discard = useCallback(() => {
    setIsConfirmingDiscard(false);
    onClose();
  }, [onClose]);

  return useMemo(
    () => ({ isSaving, isConfirmingDiscard, save, cancel, keepEditing, discard }),
    [isSaving, isConfirmingDiscard, save, cancel, keepEditing, discard]
  );
};
